import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { AppButton, AppCheckbox, AppTextField } from "../../../components/form";
import { AppFormField, AppFormGrid, AppSlideOver, PageSectionCard } from "../../../components/layout";
import { GRID_FULL, GRID_HALF } from "../../../lib/ui/formLayout";
import {
  customerPlanService,
  type FreePlan,
  type SupportPackage,
  type SupportPackagePayload,
} from "../../../services/customerPlanService";
import { QuotaFieldsEditor, QuotaSummary, formToQuota, quotaToForm, type QuotaForm } from "./quotaFields";

const eur = (value: number | null | undefined) =>
  value == null ? null : `€${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

type PackageForm = {
  name: string;
  description: string;
  priceEur: string;
  installFeeEur: string;
  tickets: string;
  meetingHours: string;
  ticketResponse: string;
  meetingResponse: string;
  termMonths: string;
  listed: boolean;
  active: boolean;
};

const num = (raw: string): number | null => {
  const v = raw.trim();
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

function toPackageForm(p: SupportPackage): PackageForm {
  return {
    name: p.name,
    description: p.description || "",
    priceEur: p.priceEur != null ? String(p.priceEur) : "",
    installFeeEur: p.installFeeEur != null ? String(p.installFeeEur) : "",
    tickets: p.tickets != null ? String(p.tickets) : "",
    meetingHours: p.meetingHours != null ? String(p.meetingHours) : "",
    ticketResponse: p.ticketResponse || "",
    meetingResponse: p.meetingResponse || "",
    termMonths: String(p.termMonths || 12),
    listed: p.listed,
    active: p.active,
  };
}

function toPackagePayload(f: PackageForm): SupportPackagePayload {
  const tickets = num(f.tickets);
  const hours = num(f.meetingHours);
  return {
    name: f.name.trim(),
    description: f.description.trim() || null,
    priceEur: num(f.priceEur),
    installFeeEur: num(f.installFeeEur),
    tickets: tickets != null ? Math.floor(tickets) : null,
    meetingHours: hours != null ? Math.floor(hours) : null,
    ticketResponse: f.ticketResponse.trim() || null,
    meetingResponse: f.meetingResponse.trim() || null,
    termMonths: Math.max(1, Math.floor(num(f.termMonths) ?? 12)),
    listed: f.listed,
    active: f.active,
  };
}

/** One support package card, laid out like the pricing page. */
function SupportCard({ pkg, onEdit }: { pkg: SupportPackage; onEdit: () => void }) {
  const { t } = useTranslation();
  const lines: [string, string][] = [];
  if (pkg.tickets != null) lines.push([t("superAdmin.support.tickets"), String(pkg.tickets)]);
  if (pkg.meetingHours != null) lines.push([t("superAdmin.support.meetingHours"), String(pkg.meetingHours)]);
  if (pkg.ticketResponse) lines.push([t("superAdmin.support.ticketResponse"), pkg.ticketResponse]);
  if (pkg.meetingResponse) lines.push([t("superAdmin.support.meetingResponse"), pkg.meetingResponse]);
  lines.push([t("superAdmin.support.term"), t("superAdmin.support.termValue", { months: pkg.termMonths })]);
  const price = eur(pkg.priceEur);
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box>
          <Typography variant="subtitle1" fontWeight={650}>
            {pkg.name}
          </Typography>
          {pkg.description && (
            <Typography variant="body2" color="text.secondary">
              {pkg.description}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.5}>
          {!pkg.active && <Chip size="small" label={t("superAdmin.support.inactive")} />}
          {!pkg.listed && <Chip size="small" variant="outlined" label={t("superAdmin.support.unlisted")} />}
        </Stack>
      </Stack>
      <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "1fr auto", columnGap: 2, rowGap: 0.25 }}>
        {lines.map(([label, value]) => (
          <Box key={label} sx={{ display: "contents" }}>
            <Typography component="dt" variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography component="dd" variant="body2" fontWeight={600} sx={{ m: 0, textAlign: "right" }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ flex: 1 }} />
      <Divider />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h6">{price ?? t("superAdmin.support.noListPrice")}</Typography>
          {pkg.installFeeEur != null && (
            <Typography variant="caption" color="text.secondary">
              {t("superAdmin.support.installFee", { amount: eur(pkg.installFeeEur) })}
            </Typography>
          )}
        </Box>
        <AppButton size="small" variant="outlined" startIcon={<EditOutlinedIcon />} onClick={onEdit}>
          {t("common.edit")}
        </AppButton>
      </Stack>
    </Box>
  );
}

/**
 * The product plan (Free; quotas apply only with the BEAS LLM / BEAS store) and the support packages sold
 * separately — as on the pricing page.
 */
export default function PlansPanel() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [plan, setPlan] = useState<FreePlan | null>(null);
  const [packages, setPackages] = useState<SupportPackage[]>([]);
  const [planOpen, setPlanOpen] = useState(false);
  const [planForm, setPlanForm] = useState<QuotaForm>(quotaToForm(null));
  const [planBeasLlm, setPlanBeasLlm] = useState(true);
  const [planNote, setPlanNote] = useState("");
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [packageForm, setPackageForm] = useState<PackageForm | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, list] = await Promise.all([customerPlanService.getFreePlan(), customerPlanService.listSupportPackages()]);
      setPlan(p);
      setPackages(Array.isArray(list) ? list : []);
    } catch {
      setError(t("superAdmin.plans.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const openPlan = () => {
    if (!plan) return;
    setPlanForm(quotaToForm(plan.quotas));
    setPlanBeasLlm(plan.includesBeasLlm);
    setPlanNote(plan.note || "");
    setPlanOpen(true);
    setError(null);
  };

  const savePlan = async () => {
    setSaving(true);
    setError(null);
    try {
      setPlan(
        await customerPlanService.updateFreePlan({
          quotas: formToQuota(planForm),
          includesBeasLlm: planBeasLlm,
          note: planNote.trim() || null,
        })
      );
      setPlanOpen(false);
      setSuccess(t("superAdmin.plans.saveSuccess"));
    } catch {
      setError(t("superAdmin.plans.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const openPackage = (pkg: SupportPackage) => {
    setEditingKey(pkg.key);
    setPackageForm(toPackageForm(pkg));
    setError(null);
  };

  const savePackage = async () => {
    if (!editingKey || !packageForm) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await customerPlanService.updateSupportPackage(editingKey, toPackagePayload(packageForm));
      setPackages((list) => list.map((p) => (p.key === saved.key ? saved : p)));
      setEditingKey(null);
      setPackageForm(null);
      setSuccess(t("superAdmin.support.saveSuccess"));
    } catch {
      setError(t("superAdmin.support.saveError"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  const setPkg = (patch: Partial<PackageForm>) => setPackageForm((f) => (f ? { ...f, ...patch } : f));

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      {error && !planOpen && !editingKey && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <PageSectionCard
        title={t("superAdmin.plans.freeTitle")}
        description={t("superAdmin.plans.freeDescription")}
        actions={
          plan ? (
            <AppButton size="small" variant="contained" startIcon={<EditOutlinedIcon />} onClick={openPlan}>
              {t("superAdmin.plans.edit")}
            </AppButton>
          ) : undefined
        }
      >
        {plan && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5">€0</Typography>
              <Chip size="small" color="success" label={t("superAdmin.plans.freeChip")} />
              {plan.includesBeasLlm ? (
                <Chip size="small" variant="outlined" label={t("superAdmin.plans.includesBeasLlm")} />
              ) : (
                <Chip size="small" color="warning" variant="outlined" label={t("superAdmin.plans.noBeasLlm")} />
              )}
            </Stack>
            <Alert severity="info">{t("superAdmin.plans.byoRule")}</Alert>
            <Box sx={{ maxWidth: 560 }}>
              <QuotaSummary values={plan.quotas} noCapLabel={t("superAdmin.plans.noCap")} />
            </Box>
            {plan.note && (
              <Typography variant="body2" color="text.secondary">
                {plan.note}
              </Typography>
            )}
          </Stack>
        )}
      </PageSectionCard>

      <PageSectionCard title={t("superAdmin.support.catalogTitle")} description={t("superAdmin.support.catalogDescription")}>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
          }}
        >
          {packages.map((pkg) => (
            <SupportCard key={pkg.key} pkg={pkg} onEdit={() => openPackage(pkg)} />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          {t("superAdmin.support.catalogFootnote")}
        </Typography>
      </PageSectionCard>

      <AppSlideOver
        open={planOpen}
        onClose={() => !saving && setPlanOpen(false)}
        size="lg"
        title={t("superAdmin.plans.editTitle")}
        actions={
          <>
            <AppButton variant="outlined" onClick={() => setPlanOpen(false)} disabled={saving}>
              {t("common.cancel")}
            </AppButton>
            <AppButton onClick={() => void savePlan()} disabled={saving} loading={saving}>
              {t("common.save")}
            </AppButton>
          </>
        }
      >
        {error && planOpen && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            {t("superAdmin.plans.editHint")}
          </Typography>
          <QuotaFieldsEditor
            form={planForm}
            onChange={(key, value) => setPlanForm((f) => ({ ...f, [key]: value }))}
            emptyHint={t("superAdmin.plans.noCap")}
          />
          <AppCheckbox
            label={t("superAdmin.plans.fieldIncludesBeasLlm")}
            checked={planBeasLlm}
            onChange={(_, checked) => setPlanBeasLlm(checked)}
          />
          <AppTextField
            label={t("superAdmin.plans.fieldNote")}
            value={planNote}
            onChange={(e) => setPlanNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </AppSlideOver>

      <AppSlideOver
        open={!!editingKey && !!packageForm}
        onClose={() => {
          if (saving) return;
          setEditingKey(null);
          setPackageForm(null);
        }}
        size="md"
        title={t("superAdmin.support.editTitle")}
        actions={
          <>
            <AppButton
              variant="outlined"
              disabled={saving}
              onClick={() => {
                setEditingKey(null);
                setPackageForm(null);
              }}
            >
              {t("common.cancel")}
            </AppButton>
            <AppButton
              onClick={() => void savePackage()}
              disabled={saving || !packageForm?.name.trim()}
              loading={saving}
            >
              {t("common.save")}
            </AppButton>
          </>
        }
      >
        {error && editingKey && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {packageForm && (
          <AppFormGrid>
            <AppFormField size={GRID_FULL}>
              <AppTextField label={t("superAdmin.support.name")} value={packageForm.name} onChange={(e) => setPkg({ name: e.target.value })} fullWidth />
            </AppFormField>
            <AppFormField size={GRID_FULL}>
              <AppTextField
                label={t("superAdmin.support.description")}
                value={packageForm.description}
                onChange={(e) => setPkg({ description: e.target.value })}
                multiline
                minRows={2}
                fullWidth
              />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppTextField label={t("superAdmin.support.priceEur")} type="number" value={packageForm.priceEur} onChange={(e) => setPkg({ priceEur: e.target.value })} helperText={t("superAdmin.support.priceHelp")} />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppTextField label={t("superAdmin.support.installFeeEur")} type="number" value={packageForm.installFeeEur} onChange={(e) => setPkg({ installFeeEur: e.target.value })} />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppTextField label={t("superAdmin.support.tickets")} type="number" value={packageForm.tickets} onChange={(e) => setPkg({ tickets: e.target.value })} />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppTextField label={t("superAdmin.support.meetingHours")} type="number" value={packageForm.meetingHours} onChange={(e) => setPkg({ meetingHours: e.target.value })} />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppTextField label={t("superAdmin.support.ticketResponse")} value={packageForm.ticketResponse} onChange={(e) => setPkg({ ticketResponse: e.target.value })} />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppTextField label={t("superAdmin.support.meetingResponse")} value={packageForm.meetingResponse} onChange={(e) => setPkg({ meetingResponse: e.target.value })} />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <AppTextField label={t("superAdmin.support.termMonths")} type="number" value={packageForm.termMonths} onChange={(e) => setPkg({ termMonths: e.target.value })} />
            </AppFormField>
            <AppFormField size={GRID_HALF}>
              <Stack>
                <AppCheckbox label={t("superAdmin.support.listed")} checked={packageForm.listed} onChange={(_, c) => setPkg({ listed: c })} />
                <AppCheckbox label={t("superAdmin.support.active")} checked={packageForm.active} onChange={(_, c) => setPkg({ active: c })} />
              </Stack>
            </AppFormField>
            <AppFormField size={GRID_FULL}>
              <Typography variant="caption" color="text.secondary">
                {t("superAdmin.support.editHint")}
              </Typography>
            </AppFormField>
          </AppFormGrid>
        )}
      </AppSlideOver>
    </Stack>
  );
}
